import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const QuoteBuilder = ({ open, onClose, customerId, conversationId, tradieProfile }) => {
  const [lineItems, setLineItems] = useState([
    { type: 'labour', description: '', uom: 'hr', qty: 1, unit_price_ex_gst: 0 }
  ]);
  const [priceMode, setPriceMode] = useState('gst_included');
  const [inclusions, setInclusions] = useState('Free quote\nQuality materials\nClean up after work');
  const [exclusions, setExclusions] = useState('Materials (quoted separately)\nPermit fees');
  const [validityDays, setValidityDays] = useState(14);
  const [creating, setCreating] = useState(false);

  const tierLimits = {
    basic: 5,
    premium: 20,
    pro: 999999
  };

  const currentLimit = tierLimits[tradieProfile?.current_tier || 'basic'];

  const addLineItem = () => {
    if (lineItems.length >= currentLimit) {
      toast.error(`Line item limit reached (${currentLimit}). Upgrade your plan to add more.`);
      return;
    }
    setLineItems([...lineItems, { type: 'labour', description: '', uom: 'hr', qty: 1, unit_price_ex_gst: 0 }]);
  };

  const removeLineItem = (index) => {
    if (lineItems.length === 1) return;
    setLineItems(lineItems.filter((_, i) => i !== index));
  };

  const updateLineItem = (index, field, value) => {
    const updated = [...lineItems];
    updated[index][field] = value;
    setLineItems(updated);
  };

  const calculateTotals = () => {
    const subtotal = lineItems.reduce((sum, item) => {
      return sum + (parseFloat(item.qty) || 0) * (parseFloat(item.unit_price_ex_gst) || 0);
    }, 0);

    let gstAmount = 0;
    let total = 0;

    if (priceMode === 'gst_included') {
      total = subtotal;
      gstAmount = total * 10 / 110; // Extract GST
    } else if (priceMode === 'plus_gst') {
      gstAmount = subtotal * 0.10;
      total = subtotal + gstAmount;
    } else {
      // no_gst
      gstAmount = 0;
      total = subtotal;
    }

    return { subtotal, gstAmount, total };
  };

  const handleCreateQuote = async () => {
    // Validate
    if (lineItems.some(item => !item.description || item.qty <= 0 || item.unit_price_ex_gst <= 0)) {
      toast.error('Please fill all line item details');
      return;
    }

    setCreating(true);
    try {
      const response = await axios.post(`${API}/quotes`, {
        customer_id: customerId,
        conversation_id: conversationId,
        line_items: lineItems,
        price_mode: priceMode,
        inclusions: inclusions.split('\n').filter(i => i.trim()),
        exclusions: exclusions.split('\n').filter(e => e.trim()),
        validity_days: validityDays
      });

      toast.success('Quote created successfully!');
      onClose();
    } catch (error) {
      console.error('Failed to create quote:', error);
      toast.error(error.response?.data?.detail || 'Failed to create quote');
    } finally {
      setCreating(false);
    }
  };

  const totals = calculateTotals();

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Professional Quote</DialogTitle>
          <DialogDescription>
            Build a detailed quote for your customer ({lineItems.length}/{currentLimit} line items)
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Line Items */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <Label className="text-base font-semibold">Line Items</Label>
              <Button
                size="sm"
                variant="outline"
                onClick={addLineItem}
                disabled={lineItems.length >= currentLimit}
                data-testid="add-line-item-button"
              >
                + Add Item
              </Button>
            </div>

            {lineItems.map((item, index) => (
              <Card key={index} className="bg-muted/30">
                <CardContent className="p-4 space-y-3">
                  <div className="flex justify-between items-start">
                    <span className="text-sm font-medium">Item {index + 1}</span>
                    {lineItems.length > 1 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeLineItem(index)}
                        className="h-6 w-6 p-0 text-destructive"
                      >
                        ✕
                      </Button>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2 space-y-1">
                      <Label className="text-xs">Description *</Label>
                      <Input
                        placeholder="e.g., Labour for bathroom renovation"
                        value={item.description}
                        onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                        className="text-sm"
                      />
                    </div>

                    <div className="space-y-1">
                      <Label className="text-xs">Type</Label>
                      <Select value={item.type} onValueChange={(value) => updateLineItem(index, 'type', value)}>
                        <SelectTrigger className="text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="labour">Labour</SelectItem>
                          <SelectItem value="materials">Materials</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-1">
                      <Label className="text-xs">UOM</Label>
                      <Select value={item.uom} onValueChange={(value) => updateLineItem(index, 'uom', value)}>
                        <SelectTrigger className="text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="hr">Hour</SelectItem>
                          <SelectItem value="item">Item</SelectItem>
                          <SelectItem value="m²">m²</SelectItem>
                          <SelectItem value="lm">Linear m</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-1">
                      <Label className="text-xs">Quantity *</Label>
                      <Input
                        type="number"
                        step="0.1"
                        min="0.1"
                        value={item.qty}
                        onChange={(e) => updateLineItem(index, 'qty', e.target.value)}
                        className="text-sm"
                      />
                    </div>

                    <div className="space-y-1">
                      <Label className="text-xs">Unit Price (ex-GST) *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={item.unit_price_ex_gst}
                        onChange={(e) => updateLineItem(index, 'unit_price_ex_gst', e.target.value)}
                        className="text-sm"
                      />
                    </div>
                  </div>

                  <div className="text-right text-sm font-medium">
                    Line Total: ${((item.qty || 0) * (item.unit_price_ex_gst || 0)).toFixed(2)}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Separator />

          {/* GST Mode */}
          <div className="space-y-2">
            <Label>GST Treatment</Label>
            <Select value={priceMode} onValueChange={setPriceMode}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="gst_included">Prices include GST</SelectItem>
                <SelectItem value="plus_gst">Prices plus GST</SelectItem>
                <SelectItem value="no_gst">No GST charged</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {priceMode === 'no_gst' && !tradieProfile?.gst_registered && 
                'You are not GST registered'}
            </p>
          </div>

          {/* Inclusions & Exclusions */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Inclusions (one per line)</Label>
              <Textarea
                value={inclusions}
                onChange={(e) => setInclusions(e.target.value)}
                rows={4}
                className="text-sm"
                placeholder="Free quote&#10;Quality materials"
              />
            </div>

            <div className="space-y-2">
              <Label>Exclusions (one per line)</Label>
              <Textarea
                value={exclusions}
                onChange={(e) => setExclusions(e.target.value)}
                rows={4}
                className="text-sm"
                placeholder="Materials (quoted separately)&#10;Permit fees"
              />
            </div>
          </div>

          {/* Validity */}
          <div className="space-y-2">
            <Label>Quote Valid For (days)</Label>
            <Input
              type="number"
              min="1"
              max="90"
              value={validityDays}
              onChange={(e) => setValidityDays(e.target.value)}
            />
          </div>

          <Separator />

          {/* Totals Preview */}
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="p-6 space-y-2">
              <div className="flex justify-between text-sm">
                <span>Subtotal:</span>
                <span className="font-medium">${totals.subtotal.toFixed(2)}</span>
              </div>
              
              {priceMode !== 'no_gst' && (
                <div className="flex justify-between text-sm">
                  <span>GST (10%):</span>
                  <span className="font-medium">${totals.gstAmount.toFixed(2)}</span>
                </div>
              )}
              
              <Separator />
              
              <div className="flex justify-between text-lg font-bold">
                <span>Total {priceMode === 'gst_included' || priceMode === 'plus_gst' ? '(inc GST)' : ''}:</span>
                <span>${totals.total.toFixed(2)}</span>
              </div>
              
              {priceMode === 'gst_included' && (
                <p className="text-xs text-muted-foreground">GST included: ${totals.gstAmount.toFixed(2)}</p>
              )}
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1"
              disabled={creating}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateQuote}
              className="flex-1 bg-primary hover:bg-primary-hover"
              disabled={creating}
              data-testid="create-quote-submit"
            >
              {creating ? 'Creating...' : 'Create Quote'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const QuoteViewer = ({ quoteId, onClose }) => {
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (quoteId) {
      fetchQuote();
    }
  }, [quoteId]);

  const fetchQuote = async () => {
    try {
      const response = await axios.get(`${API}/quotes/${quoteId}`);
      setQuote(response.data);
    } catch (error) {
      console.error('Failed to fetch quote:', error);
      toast.error('Failed to load quote');
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    try {
      const response = await axios.get(`${API}/quotes/${quoteId}/pdf`);
      const pdfData = response.data.pdf_base64;
      const blob = new Blob([Uint8Array.from(atob(pdfData), c => c.charCodeAt(0))], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.data.filename;
      a.click();
      toast.success('PDF downloaded');
    } catch (error) {
      console.error('Failed to download PDF:', error);
      toast.error('Failed to download PDF');
    }
  };

  const handleAccept = async () => {
    try {
      await axios.patch(`${API}/quotes/${quoteId}/accept`);
      toast.success('Quote accepted!');
      fetchQuote();
    } catch (error) {
      console.error('Failed to accept quote:', error);
      toast.error('Failed to accept quote');
    }
  };

  const handleDecline = async () => {
    try {
      await axios.patch(`${API}/quotes/${quoteId}/decline`);
      toast.success('Quote declined');
      fetchQuote();
    } catch (error) {
      console.error('Failed to decline quote:', error);
      toast.error('Failed to decline quote');
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
        </CardContent>
      </Card>
    );
  }

  if (!quote) return null;

  const getStatusBadge = (status) => {
    const badges = {
      sent: <Badge variant="outline" className="bg-blue-50">📤 Sent</Badge>,
      viewed: <Badge variant="outline" className="bg-purple-50">👁️ Viewed</Badge>,
      accepted: <Badge className="bg-green-500">✓ Accepted</Badge>,
      declined: <Badge variant="destructive">✗ Declined</Badge>,
    };
    return badges[status] || badges.sent;
  };

  return (
    <Card className="border-2 border-primary/20">
      <CardContent className="p-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <h3 className="text-2xl font-bold">Quote #{quote.quote_number}</h3>
            <p className="text-sm text-muted-foreground">
              Issued {new Date(quote.issue_date).toLocaleDateString()} • 
              Valid until {new Date(quote.expiry_date).toLocaleDateString()}
            </p>
          </div>
          {getStatusBadge(quote.status)}
        </div>

        <Separator />

        {/* Line Items */}
        <div className="space-y-3">
          <Label className="text-base font-semibold">Items</Label>
          <div className="space-y-2">
            {quote.line_items.map((item, index) => (
              <div key={index} className="flex justify-between items-start p-3 bg-muted/50 rounded-lg">
                <div className="flex-1">
                  <p className="font-medium">{item.description}</p>
                  <p className="text-sm text-muted-foreground">
                    {item.qty} {item.uom} × ${item.unit_price_ex_gst.toFixed(2)}
                  </p>
                </div>
                <span className="font-medium">
                  ${(item.qty * item.unit_price_ex_gst).toFixed(2)}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Inclusions/Exclusions */}
        <div className="grid grid-cols-2 gap-4">
          {quote.inclusions && quote.inclusions.length > 0 && (
            <div className="space-y-2">
              <Label className="text-sm font-semibold flex items-center gap-1">
                <span className="text-green-500">✓</span> Inclusions
              </Label>
              <ul className="space-y-1">
                {quote.inclusions.map((item, i) => (
                  <li key={i} className="text-sm text-muted-foreground">• {item}</li>
                ))}
              </ul>
            </div>
          )}

          {quote.exclusions && quote.exclusions.length > 0 && (
            <div className="space-y-2">
              <Label className="text-sm font-semibold flex items-center gap-1">
                <span className="text-muted-foreground">✗</span> Exclusions
              </Label>
              <ul className="space-y-1">
                {quote.exclusions.map((item, i) => (
                  <li key={i} className="text-sm text-muted-foreground">• {item}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <Separator />

        {/* Totals */}
        <div className="space-y-2 bg-primary/5 p-4 rounded-lg">
          <div className="flex justify-between text-sm">
            <span>Subtotal:</span>
            <span className="font-medium">${quote.subtotal.toFixed(2)}</span>
          </div>
          
          {quote.price_mode !== 'no_gst' && (
            <div className="flex justify-between text-sm">
              <span>GST (10%):</span>
              <span className="font-medium">${quote.gst_amount.toFixed(2)}</span>
            </div>
          )}
          
          <Separator />
          
          <div className="flex justify-between text-lg font-bold">
            <span>Total:</span>
            <span>${quote.total.toFixed(2)}</span>
          </div>
          
          {quote.price_mode === 'gst_included' && (
            <p className="text-xs text-muted-foreground text-right">Includes GST: ${quote.gst_amount.toFixed(2)}</p>
          )}
          {quote.price_mode === 'plus_gst' && (
            <p className="text-xs text-muted-foreground text-right">Plus GST</p>
          )}
          {quote.price_mode === 'no_gst' && (
            <p className="text-xs text-muted-foreground text-right">No GST charged</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={downloadPDF}
            className="flex-1"
            data-testid="download-quote-pdf"
          >
            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Download PDF
          </Button>
          
          {quote.status === 'sent' || quote.status === 'viewed' ? (
            <>
              <Button
                onClick={handleAccept}
                className="flex-1 bg-green-500 hover:bg-green-600"
                data-testid="accept-quote-button"
              >
                ✓ Accept Quote
              </Button>
              <Button
                variant="destructive"
                onClick={handleDecline}
                className="flex-1"
                data-testid="decline-quote-button"
              >
                ✗ Decline
              </Button>
            </>
          ) : null}
        </div>

        {quote.status === 'accepted' && (
          <Card className="bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800">
            <CardContent className="p-4 text-center">
              <p className="text-green-700 dark:text-green-300 font-medium">
                ✓ Quote accepted on {new Date(quote.updated_at).toLocaleDateString()}
              </p>
            </CardContent>
          </Card>
        )}
      </CardContent>
    </Card>
  );
};
